import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import VehicleSubstitutionsTab from '../quota-allocation-pricing-center/components/VehicleSubstitutionsTab';
import ApiLabTab from './components/ApiLabTab';
import DoFinanceTab from './components/DoFinanceTab';
import AdminReviewQueueTab from './components/AdminReviewQueueTab';
import VisitorStatsTab from './components/VisitorStatsTab';
import {
  PERMISSION_KEYS,
  getPermissionMapForUsers,
  setPermission,
} from '../../data/userPermissions';

// ── Role config ──────────────────────────────────────────────
// 'admin' deliberately excluded — cannot be assigned via UI
const ROLE_OPTIONS = [
  { value: 'buyer',         label: 'Buyer' },
  { value: 'seller',        label: 'Seller' },
  { value: 'monitor',       label: 'Monitor' },
  { value: 'quota_manager', label: 'Quota Manager' },
  { value: 'transporter',   label: 'Transporter' },
  { value: 'sales_agent',   label: 'Sales Agent' },
  { value: 'factory_admin', label: 'Factory Admin' },
  { value: 'factory_relations_officer', label: 'Factory Relations Officer' },
];

const ROLE_COLORS = {
  buyer:         { bg: '#dbeafe', color: '#1d4ed8' },
  seller:        { bg: '#fce7f3', color: '#9d174d' },
  monitor:       { bg: '#ede9fe', color: '#7c3aed' },
  quota_manager: { bg: '#fef3c7', color: '#92400e' },
  transporter:   { bg: '#d1fae5', color: '#065f46' },
  sales_agent:   { bg: '#ffedd5', color: '#c2410c' },
  factory_admin: { bg: '#fee2e2', color: '#991b1b' },
  factory_relations_officer: { bg: '#e0f2fe', color: '#0369a1' },
  admin:         { bg: '#f3f4f6', color: '#1f2937' },
};

const AdminDashboard = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [allUsers,          setAllUsers]          = useState([]);
  const [usersLoading,      setUsersLoading]      = useState(true);
  const [toast,             setToast]             = useState(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [fdPermissionSaving, setFdPermissionSaving] = useState(null); // user_id being saved
  const [dfPermissionSaving, setDfPermissionSaving] = useState(null); // user_id being saved
  const [bulkDoFinanceSaving, setBulkDoFinanceSaving] = useState(false);
  const [roleFilter,        setRoleFilter]        = useState('all');
  const [dateFilter,        setDateFilter]        = useState('till-date');
  const [fromDate,          setFromDate]          = useState('');
  const [toDate,            setToDate]            = useState('');
  const [analytics,         setAnalytics]         = useState({
    totalOrders:       0,
    totalSugarMT:      0,
    totalActiveBuyers: 0,
    totalMonitors:     0,
  });

  // ── Vehicle Lookup state ─────────────────────────────────────
  const [vehicleNumber,        setVehicleNumber]        = useState('');
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleData,          setVehicleData]          = useState(null);
  const [vehicleLookupError,   setVehicleLookupError]   = useState('');  
  const [dlNumber,             setDlNumber]             = useState('');
  const [dlDob,                setDlDob]                = useState('');
  const [driverData,           setDriverData]           = useState(null);
  const [driverLoading,        setDriverLoading]        = useState(false);
  const [driverError,          setDriverError]          = useState('');
  const [showLogoutConfirm,    setShowLogoutConfirm]    = useState(false);
  const [activeTab,            setActiveTab]            = useState(() => localStorage.getItem('adminActiveTab') || 'overview');

  // Persist active tab across navigation
  React.useEffect(() => { localStorage.setItem('adminActiveTab', activeTab); }, [activeTab]);

  // ── Map refs (Admin Vehicle Lookup) ─────────────────────────
  const adminMapContainerRef = useRef(null);
  const adminLeafletMapRef   = useRef(null);

  // ── Toast ────────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch all users from user_profiles ───────────────────────
  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(
          'user_id, full_name, mobile_phone, email, role, company_name, gstin, pan, city, is_active, is_archived, created_at, last_login_at'
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      const users = data || [];
      const ids = users.map((u) => u?.user_id).filter(Boolean);
      const fdMap = await getPermissionMapForUsers(
        PERMISSION_KEYS.INTERNAL_FACTORY_DIRECTORY,
        ids
      );
      const dfMap = await getPermissionMapForUsers(
        PERMISSION_KEYS.DO_FINANCE,
        ids
      );
      return users.map((u) => ({
        ...u,
        // Admins always have access; toggle still shown as On for transparency
        factory_directory_enabled:
          u?.role === 'admin' ? true : !!fdMap[u?.user_id],
        // No role-based override — defaults to disabled for every user
        // (including admins) until explicitly enabled.
        do_finance_enabled: !!dfMap[u?.user_id],
      }));
    } catch (err) {
      console.error('fetchAllUsers error:', err);
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  // ── Fetch analytics from buyer_bookings ───────────────────────
  const fetchAnalytics = async (users) => {
    try {
      const { data: bookings, error } = await supabase
        .from('buyer_bookings')
        .select('quantity');
      if (error) throw error;

      const totalOrders       = bookings?.length || 0;
      const totalSugarMT      = bookings?.reduce((sum, b) => sum + (parseFloat(b?.quantity) || 0), 0) || 0;
      const totalActiveBuyers = users.filter(u => u?.role === 'buyer' && u?.is_active).length;
      const totalMonitors     = users.filter(u => u?.role === 'monitor').length;

      setAnalytics({ totalOrders, totalSugarMT, totalActiveBuyers, totalMonitors });
    } catch (err) {
      console.error('fetchAnalytics error:', err);
    }
  };

  // ── Load everything ──────────────────────────────────────────
  const loadData = async () => {
    const users = await fetchAllUsers();
    setAllUsers(users);
    await fetchAnalytics(users);
  };

  useEffect(() => {
    loadData();
  }, []);

  // ── Load Leaflet CSS/JS for admin map ────────────────────────
  useEffect(() => {
    if (document.getElementById('admin-leaflet-css')) return;
    const lfCSS = document.createElement('link');
    lfCSS.rel = 'stylesheet';
    lfCSS.id  = 'admin-leaflet-css';
    lfCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(lfCSS);
    const lfScript = document.createElement('script');
    lfScript.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    lfScript.id  = 'admin-leaflet-js';
    lfScript.async = true;
    document.body.appendChild(lfScript);
  }, []);

  // ── Admin map: init when vehicleData toll crossings arrive ───
  useEffect(() => {
    const crossings = vehicleData?.toll?.crossings;
    if (!crossings || crossings.length === 0) return;

    const parseGeocode = (geo) => {
      if (!geo) return null;
      const parts = geo.split(',').map(s => parseFloat(s.trim()));
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
      return [parts[0], parts[1]];
    };

    const initAdminMap = () => {
      if (!adminMapContainerRef.current || !window.L) return;
      const L = window.L;
      if (adminLeafletMapRef.current) {
        adminLeafletMapRef.current.remove();
        adminLeafletMapRef.current = null;
      }
      const coords = crossings.map(tc => parseGeocode(tc.tollPlazaGeocode)).filter(Boolean);
      if (!coords.length) return;

      const map = L.map(adminMapContainerRef.current, {
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 18,
        subdomains: 'abcd',
      }).addTo(map);

      if (coords.length > 1) {
        L.polyline(coords, { color: '#f59e0b', weight: 3, opacity: 0.9, dashArray: '8, 5' }).addTo(map);
      }

      crossings.forEach((tc, i) => {
        const coord = parseGeocode(tc.tollPlazaGeocode);
        if (!coord) return;
        const isLatest = i === crossings.length - 1;
        const size = isLatest ? 36 : 26;
        const icon = L.divIcon({
          className: '',
          html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${isLatest ? '#10b981' : '#f59e0b'};border:3px solid ${isLatest ? '#34d399' : '#fbbf24'};display:flex;align-items:center;justify-content:center;font-size:${isLatest ? '15px' : '10px'};font-weight:800;color:${isLatest ? '#fff' : '#0f172a'};box-shadow:0 0 ${isLatest ? '16px rgba(16,185,129,0.75)' : '6px rgba(245,158,11,0.5)'}">${isLatest ? '🚛' : i + 1}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
        L.marker(coord, { icon }).addTo(map).bindPopup(
          L.popup({ maxWidth: 220 }).setContent(`<div style="padding:10px 14px;font-family:sans-serif;"><div style="color:#f59e0b;font-size:11px;font-weight:700;text-transform:uppercase;margin-bottom:4px">${isLatest ? '📍 Last seen' : 'Stop ' + (i+1)}</div><div style="font-size:14px;font-weight:800;margin-bottom:4px">${tc.tollPlazaName || 'Toll Plaza'}</div><div style="color:#6b7280;font-size:12px">${tc.readerReadTime || ''}</div><div style="color:#6b7280;font-size:12px">Dir: ${tc.laneDirection || '—'}</div></div>`)
        );
      });

      if (coords.length === 1) {
        map.setView(coords[0], 13);
      } else {
        map.fitBounds(L.latLngBounds(coords), { padding: [40, 40] });
      }
      adminLeafletMapRef.current = map;
    };

    if (window.L) {
      initAdminMap();
    } else {
      const timer = setInterval(() => {
        if (window.L) { clearInterval(timer); initAdminMap(); }
      }, 150);
      return () => clearInterval(timer);
    }
  }, [vehicleData]);

  // ── Role change — auto-save on dropdown change ────────────────
  const handleRoleChange = async (userId, newRole) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole })
        .eq('user_id', userId);
      if (error) throw error;

      const updatedUsers = allUsers.map(u =>
        u?.user_id === userId ? { ...u, role: newRole } : u
      );
      setAllUsers(updatedUsers);
      fetchAnalytics(updatedUsers);

      const label = ROLE_OPTIONS.find(r => r.value === newRole)?.label || newRole;
      showToast(`Role updated to ${label}`, 'success');
    } catch (err) {
      console.error('Role change error:', err);
      showToast('Failed to update role. Please try again.', 'error');
    }
  };

  // ── Factory Directory secondary permission toggle ─────────────
  const handleFactoryDirectoryToggle = async (user, nextEnabled) => {
    if (!user?.user_id) return;
    if (user?.role === 'admin') {
      showToast('Admins always have Factory Directory access', 'success');
      return;
    }
    setFdPermissionSaving(user.user_id);
    // Optimistic UI
    setAllUsers((prev) =>
      prev.map((u) =>
        u?.user_id === user.user_id
          ? { ...u, factory_directory_enabled: !!nextEnabled }
          : u
      )
    );
    try {
      const { error } = await setPermission(
        user.user_id,
        PERMISSION_KEYS.INTERNAL_FACTORY_DIRECTORY,
        !!nextEnabled
      );
      if (error) throw error;
      showToast(
        nextEnabled
          ? `Factory Directory enabled for ${user.full_name || user.email || 'user'}`
          : `Factory Directory disabled for ${user.full_name || user.email || 'user'}`,
        'success'
      );
    } catch (err) {
      console.error('Factory Directory permission error:', err);
      // Revert
      setAllUsers((prev) =>
        prev.map((u) =>
          u?.user_id === user.user_id
            ? { ...u, factory_directory_enabled: !nextEnabled }
            : u
        )
      );
      showToast('Failed to update Factory Directory access. Please try again.', 'error');
    } finally {
      setFdPermissionSaving(null);
    }
  };

  // ── DO Finance secondary permission toggle ─────────────────────
  const handleDoFinanceToggle = async (user, nextEnabled) => {
    if (!user?.user_id) return;
    setDfPermissionSaving(user.user_id);
    // Optimistic UI
    setAllUsers((prev) =>
      prev.map((u) =>
        u?.user_id === user.user_id
          ? { ...u, do_finance_enabled: !!nextEnabled }
          : u
      )
    );
    try {
      const { error } = await setPermission(
        user.user_id,
        PERMISSION_KEYS.DO_FINANCE,
        !!nextEnabled
      );
      if (error) throw error;
      showToast(
        nextEnabled
          ? `DO Finance enabled for ${user.full_name || user.email || 'user'}`
          : `DO Finance disabled for ${user.full_name || user.email || 'user'}`,
        'success'
      );
    } catch (err) {
      console.error('DO Finance permission error:', err);
      // Revert
      setAllUsers((prev) =>
        prev.map((u) =>
          u?.user_id === user.user_id
            ? { ...u, do_finance_enabled: !nextEnabled }
            : u
        )
      );
      showToast('Failed to update DO Finance access. Please try again.', 'error');
    } finally {
      setDfPermissionSaving(null);
    }
  };

  // ── DO Finance bulk "Enable for All" (visible/filtered users) ──
  const handleEnableDoFinanceForAll = async () => {
    const targets = filteredUsers.filter((u) => !u?.do_finance_enabled);
    if (!targets.length) {
      showToast('DO Finance is already enabled for all visible users', 'success');
      return;
    }
    const confirmed = window.confirm(
      `Enable DO Finance for all ${targets.length} visible user(s)? This grants access immediately.`
    );
    if (!confirmed) return;

    setBulkDoFinanceSaving(true);
    const succeededIds = [];
    let failCount = 0;

    for (const u of targets) {
      const { error } = await setPermission(u.user_id, PERMISSION_KEYS.DO_FINANCE, true);
      if (error) {
        failCount += 1;
        console.error('Bulk DO Finance enable error:', u.user_id, error);
      } else {
        succeededIds.push(u.user_id);
      }
    }

    if (succeededIds.length) {
      setAllUsers((prev) =>
        prev.map((u) =>
          succeededIds.includes(u.user_id) ? { ...u, do_finance_enabled: true } : u
        )
      );
    }

    setBulkDoFinanceSaving(false);

    if (failCount === 0) {
      showToast(`DO Finance enabled for ${succeededIds.length} user(s)`, 'success');
    } else if (succeededIds.length === 0) {
      showToast(`Failed to enable DO Finance for ${failCount} user(s)`, 'error');
    } else {
      showToast(
        `DO Finance enabled for ${succeededIds.length} user(s), failed for ${failCount}`,
        'error'
      );
    }
  };

  // ── Soft delete — toggle is_active ───────────────────────────
  const handleToggleActive = async (userId, currentState) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: !currentState })
        .eq('user_id', userId);
      if (error) throw error;

      const updatedUsers = allUsers.map(u =>
        u?.user_id === userId ? { ...u, is_active: !currentState } : u
      );
      setAllUsers(updatedUsers);
      fetchAnalytics(updatedUsers);
      setDeactivateConfirm(null);
      showToast(currentState ? 'User deactivated' : 'User activated', 'success');
    } catch (err) {
      console.error('Toggle active error:', err);
      showToast('Failed to update user status. Please try again.', 'error');
    }
  };

  // ── Archive — toggle is_archived ─────────────────────────────
  const handleToggleArchive = async (userId, currentState) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_archived: !currentState })
        .eq('user_id', userId);
      if (error) throw error;

      const updatedUsers = allUsers.map(u =>
        u?.user_id === userId ? { ...u, is_archived: !currentState } : u
      );
      setAllUsers(updatedUsers);
      setArchiveConfirm(null);
      showToast(currentState ? 'User unarchived' : 'User archived', 'success');
    } catch (err) {
      console.error('Toggle archive error:', err);
      showToast('Failed to update archive status. Please try again.', 'error');
    }
  };

  // ── Vehicle Lookup ───────────────────────────────────────────
  const handleVehicleLookup = async () => {
    if (!vehicleNumber.trim()) return;
    setVehicleLookupLoading(true);
    setVehicleData(null);
    setVehicleLookupError('');
    try {
      const vNum = vehicleNumber.trim().toUpperCase().replace(/\s/g, '');
      const [vahanRes, fastagRes, tollRes] = await Promise.all([
        supabase.functions.invoke('verify-vehicle',      { body: { vehicleNumber: vNum } }),
        supabase.functions.invoke('check-fastag-status', { body: { vehicleNumber: vNum } }),
        supabase.functions.invoke('poll-toll-crossings', { body: { vehicleNumber: vNum } }),
      ]);
      // Sort crossings chronologically so truck icon lands on most-recent timestamp
      const rawCrossings = tollRes.data?.crossings || [];
      const sortedCrossings = [...rawCrossings].sort((a, b) =>
        new Date(a.readerReadTime) - new Date(b.readerReadTime)
      );
      setVehicleData({
        vahan:  vahanRes.data?.data  || null,
        fastag: fastagRes.data || null,
        toll:   tollRes.data ? { ...tollRes.data, crossings: sortedCrossings } : null,
      });
    } catch (err) {
      console.error('Vehicle lookup error:', err);
      setVehicleLookupError('Lookup failed. Please try again.');
    } finally {
      setVehicleLookupLoading(false);
    }
  };

  // ── Driver Lookup ────────────────────────────────────────────
  const handleDriverLookup = async () => {
    if (!dlNumber.trim()) return;
    setDriverLoading(true);
    setDriverData(null);
    setDriverError('');
    try {
      const { data, error } = await supabase.functions.invoke('verify-driver', {
        body: { dl_licno: dlNumber.trim().toUpperCase(), dob: dlDob },        
      });
      if (error) throw error;
      setDriverData(data);
    } catch (err) {
      console.error('Driver lookup error:', err);
      setDriverError('Driver lookup failed. Please try again.');
    } finally {
      setDriverLoading(false);
    }
  };

  const handleLogout = () => setShowLogoutConfirm(true);

  // Back button guard — shows logout confirm instead of navigating away
  useEffect(() => {
    window.history.pushState({ sugarHathiGuard: true }, '');
    const handlePopState = () => {
      window.history.pushState({ sugarHathiGuard: true }, '');
      setShowLogoutConfirm(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    localStorage.removeItem('userRole');
    localStorage.removeItem('userEmail');
    navigate('/login-authentication');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d?.getTime())) return dateStr;
    return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ── Derived: filtered users for current tab ──────────────────
  const filteredUsers = allUsers.filter(u => {
    if (roleFilter === 'archived') return u?.is_archived === true;
    if (u?.is_archived === true) return false;
    return roleFilter === 'all' ? true : u?.role === roleFilter;
  });

  // ── Role filter tabs ─────────────────────────────────────────
  const activeUsers = allUsers.filter(u => !u?.is_archived);
  const roleTabs = [
    { key: 'all',           label: 'All Users',       count: activeUsers.length },
    { key: 'buyer',         label: 'Buyers',          count: activeUsers.filter(u => u?.role === 'buyer').length },
    { key: 'monitor',       label: 'Monitors',        count: activeUsers.filter(u => u?.role === 'monitor').length },
    { key: 'quota_manager', label: 'Quota Managers',  count: activeUsers.filter(u => u?.role === 'quota_manager').length },
    { key: 'seller',        label: 'Sellers',         count: activeUsers.filter(u => u?.role === 'seller').length },
    { key: 'transporter',   label: 'Transporters',    count: activeUsers.filter(u => u?.role === 'transporter').length },
    { key: 'sales_agent',   label: 'Sales Agents',    count: activeUsers.filter(u => u?.role === 'sales_agent').length },
    { key: 'factory_admin', label: 'Factory Admins',  count: activeUsers.filter(u => u?.role === 'factory_admin').length },
    { key: 'factory_relations_officer', label: 'Factory Relations Officers', count: activeUsers.filter(u => u?.role === 'factory_relations_officer').length },
    { key: 'archived',      label: 'Archived',        count: allUsers.filter(u => u?.is_archived).length },
  ];

  // ── Analytics cards ──────────────────────────────────────────
  const analyticsCards = [
    { label: 'Total Orders',    value: analytics?.totalOrders,       icon: 'ShoppingCart', color: '#1e4a72', bg: '#dbeafe' },
    { label: 'Total Sugar MT',  value: `${analytics?.totalSugarMT?.toLocaleString('en-IN')} MT`, icon: 'Package', color: '#92400e', bg: '#fef3c7' },
    { label: 'Active Buyers',   value: analytics?.totalActiveBuyers, icon: 'Users',        color: '#065f46', bg: '#d1fae5' },
    { label: 'Monitors',        value: analytics?.totalMonitors,     icon: 'Eye',          color: '#7c3aed', bg: '#ede9fe' },
  ];

  // ────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Admin Dashboard - SugarHathi</title>
        <meta name="description" content="Admin control panel for SugarHathi platform." />
      </Helmet>

      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium"
          style={{
            backgroundColor: toast?.type === 'success' ? '#d1fae5' : '#fee2e2',
            color:           toast?.type === 'success' ? '#065f46' : '#991b1b',
          }}
        >
          <Icon name={toast?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={16} />
          {toast?.msg}
        </div>
      )}

      {/* Deactivate / Activate Confirmation Modal */}
      {deactivateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: deactivateConfirm?.is_active ? '#fee2e2' : '#d1fae5' }}
              >
                <Icon
                  name={deactivateConfirm?.is_active ? 'UserX' : 'UserCheck'}
                  size={20}
                  color={deactivateConfirm?.is_active ? '#dc2626' : '#059669'}
                />
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: '#1f2937' }}>
                  {deactivateConfirm?.is_active ? 'Deactivate User' : 'Activate User'}
                </h3>
                <p className="text-sm" style={{ color: '#6b7280' }}>
                  {deactivateConfirm?.is_active
                    ? 'User will lose platform access immediately.'
                    : 'User will regain platform access.'}
                </p>
              </div>
            </div>
            <p className="text-sm mb-5" style={{ color: '#374151' }}>
              Are you sure you want to{' '}
              {deactivateConfirm?.is_active ? 'deactivate' : 'activate'}{' '}
              <strong>{deactivateConfirm?.full_name}</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeactivateConfirm(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition hover:bg-gray-50"
                style={{ borderColor: '#e5e7eb', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleToggleActive(deactivateConfirm?.user_id, deactivateConfirm?.is_active)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: deactivateConfirm?.is_active ? '#dc2626' : '#059669' }}
              >
                {deactivateConfirm?.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive / Unarchive Confirmation Modal */}
      {archiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: archiveConfirm?.is_archived ? '#d1fae5' : '#e0e7ff' }}
              >
                <Icon
                  name={archiveConfirm?.is_archived ? 'ArchiveRestore' : 'Archive'}
                  size={20}
                  color={archiveConfirm?.is_archived ? '#059669' : '#3730a3'}
                />
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: '#1f2937' }}>
                  {archiveConfirm?.is_archived ? 'Unarchive User' : 'Archive User'}
                </h3>
                <p className="text-sm" style={{ color: '#6b7280' }}>
                  {archiveConfirm?.is_archived
                    ? 'User will return to the active list.'
                    : 'User will be hidden from active lists. No data is deleted.'}
                </p>
              </div>
            </div>
            <p className="text-sm mb-5" style={{ color: '#374151' }}>
              Are you sure you want to{' '}
              {archiveConfirm?.is_archived ? 'unarchive' : 'archive'}{' '}
              <strong>{archiveConfirm?.full_name}</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setArchiveConfirm(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition hover:bg-gray-50"
                style={{ borderColor: '#e5e7eb', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleToggleArchive(archiveConfirm?.user_id, archiveConfirm?.is_archived)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: archiveConfirm?.is_archived ? '#059669' : '#4338ca' }}
              >
                {archiveConfirm?.is_archived ? 'Unarchive' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen" style={{ backgroundColor: '#f8f1e9' }}>

        {/* Header */}
        <header className="bg-white border-b shadow-sm sticky top-0 z-40" style={{ borderColor: '#e5e7eb' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl overflow-hidden shadow-sm ring-1 ring-amber-200 cursor-pointer" onClick={() => navigate('/')} title="SugarHathi home">
                <img
                  src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png"
                  alt="SugarHathi"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-base font-bold leading-tight" style={{ color: '#1e4a72' }}>SugarHathi</h1>
                <p className="text-xs" style={{ color: '#6b7280' }}>Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
              >
                <Icon name="ShieldCheck" size={13} />
                Admin
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition hover:bg-gray-50"
                style={{ borderColor: '#e5e7eb', color: '#6b7280' }}
              >
                <Icon name="LogOut" size={14} />
                {t('common.logout')}
              </button>
            </div>
          </div>
        </header>


        {/* ── Top-level tab navigation ── */}
        <div className="bg-white border-b sticky top-[57px] z-30" style={{ borderColor: '#e5e7eb' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-0 overflow-x-auto">
            {[
              { key: 'overview',   label: 'Overview',    icon: 'LayoutDashboard' },
              { key: 'vehicles',   label: 'Vehicles',    icon: 'Truck' },
              { key: 'do_finance', label: 'DO Finance',  icon: 'Gavel' },
              { key: 'api_lab',    label: 'API Lab',     icon: 'FlaskConical' },
              { key: 'scraper_control', label: 'Scraper Control', icon: 'Bot', href: '/scraper-control' },
              { key: 'review_queue', label: 'Review Queue', icon: 'ClipboardCheck' },
              { key: 'visitor_stats', label: 'Visitor Stats', icon: 'MapPin' },
              { key: 'factory_directory', label: 'Factory Directory', icon: 'BookOpen', href: '/internal-factory-directory' },
              { key: 'factory_rate_intelligence', label: 'Factory Rates', icon: 'TrendingUp', href: '/factory-rate-intelligence-center' },
              { key: 'rate_approval_queue', label: 'Rate Approval', icon: 'CheckSquare', href: '/rate-approval-queue' },
              { key: 'submission_history', label: 'Submission History', icon: 'History', href: '/submission-history' },
              { key: 'live_tender_info', label: 'Live Tender Info', icon: 'FileText', href: '/admin-live-tender-info' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  if (tab.href) {
                    navigate(tab.href);
                    return;
                  }
                  setActiveTab(tab.key);
                }}
                className="flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition"
                style={{
                  borderBottomColor: activeTab === tab.key ? '#1e4a72' : 'transparent',
                  color: activeTab === tab.key ? '#1e4a72' : '#6b7280',
                  backgroundColor: 'transparent',
                }}
              >
                <Icon name={tab.icon} size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-8">

          {activeTab === 'overview' && (<>
          {/* ── Section 1: Analytics ── */}
          <section>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>Analytics</h2>
                <p className="text-sm" style={{ color: '#6b7280' }}>Platform overview</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={dateFilter}
                  onChange={e => setDateFilter(e?.target?.value)}
                  className="text-sm px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: '#d1d5db', backgroundColor: '#fff', color: '#374151' }}
                >
                  <option value="till-date">Till Date</option>
                  <option value="custom">Custom Range</option>
                </select>
                {dateFilter === 'custom' && (
                  <>
                    <input
                      type="date" value={fromDate}
                      onChange={e => setFromDate(e?.target?.value)}
                      className="text-sm px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-amber-400"
                      style={{ borderColor: '#d1d5db', backgroundColor: '#fff', color: '#374151' }}
                    />
                    <span className="text-sm" style={{ color: '#6b7280' }}>to</span>
                    <input
                      type="date" value={toDate}
                      onChange={e => setToDate(e?.target?.value)}
                      className="text-sm px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-amber-400"
                      style={{ borderColor: '#d1d5db', backgroundColor: '#fff', color: '#374151' }}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {analyticsCards?.map(card => (
                <div key={card?.label} className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: card?.bg }}>
                      <Icon name={card?.icon} size={20} color={card?.color} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>{card?.value}</p>
                  <p className="text-xs mt-1 font-medium" style={{ color: '#6b7280' }}>{card?.label}</p>
                  {dateFilter === 'custom' && fromDate && toDate && (
                    <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>{fromDate} – {toDate}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* ── Section 2: All Users ── */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>All Users</h2>
                <p className="text-sm" style={{ color: '#6b7280' }}>Manage roles and access for all registered users</p>
              </div>
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold"
                style={{ backgroundColor: '#dbeafe', color: '#1e4a72' }}
              >
                <Icon name="Users" size={14} />
                {allUsers?.length} Total
              </div>
            </div>

            {/* Role Filter Tabs */}
            <div className="flex gap-2 flex-wrap mb-4">
              {roleTabs?.map(tab => (
                <button
                  key={tab?.key}
                  onClick={() => setRoleFilter(tab?.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition"
                  style={{
                    backgroundColor: roleFilter === tab?.key ? '#1e4a72' : '#fff',
                    color:           roleFilter === tab?.key ? '#fff'    : '#374151',
                    borderColor:     roleFilter === tab?.key ? '#1e4a72' : '#e5e7eb',
                  }}
                >
                  {tab?.label}
                  <span
                    className="px-1.5 py-0.5 rounded-full text-xs"
                    style={{
                      backgroundColor: roleFilter === tab?.key ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                      color:           roleFilter === tab?.key ? '#fff' : '#6b7280',
                    }}
                  >
                    {tab?.count}
                  </span>
                </button>
              ))}
            </div>

            {/* DO Finance bulk action */}
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={handleEnableDoFinanceForAll}
                disabled={bulkDoFinanceSaving || usersLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition disabled:opacity-50"
                style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }}
              >
                <Icon name="Zap" size={13} />
                {bulkDoFinanceSaving ? 'Enabling DO Finance...' : 'Enable DO Finance for All (visible)'}
              </button>
            </div>

            {usersLoading ? (
              <div className="bg-white rounded-2xl border shadow-sm p-12 flex items-center justify-center" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm" style={{ color: '#9ca3af' }}>Loading users...</p>
                </div>
              </div>
            ) : filteredUsers?.length === 0 ? (
              <div className="bg-white rounded-2xl border shadow-sm p-12 text-center" style={{ borderColor: '#e5e7eb' }}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#f3f4f6' }}>
                  <Icon name="Users" size={28} color="#9ca3af" />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: '#374151' }}>No users found</h3>
                <p className="text-sm" style={{ color: '#9ca3af' }}>No users match the selected filter.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: '#fef3c7' }}>
                          {['Name', 'Mobile', 'Email', 'Company', 'GSTIN', 'Role', 'Factory Directory', 'DO Finance', 'Status', 'Registered', 'Action']?.map(h => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide whitespace-nowrap"
                              style={{ color: '#92400e' }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers?.map((user, idx) => {
                          const roleColor = ROLE_COLORS[user?.role] || ROLE_COLORS.buyer;
                          return (
                            <tr
                              key={user?.user_id}
                              className="border-t hover:bg-amber-50/30 transition-colors"
                              style={{
                                borderColor:     '#f3f4f6',
                                backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5',
                              }}
                            >
                              {/* Name */}
                              <td className="px-4 py-3 font-medium whitespace-nowrap" style={{ color: '#1f2937' }}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                                    style={{ backgroundColor: '#1e4a72' }}
                                  >
                                    {user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <span style={{ opacity: user?.is_active ? 1 : 0.45 }}>
                                    {user?.full_name || '—'}
                                  </span>
                                </div>
                              </td>

                              {/* Mobile */}
                              <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: '#374151' }}>
                                {user?.mobile_phone || <span style={{ color: '#9ca3af' }}>—</span>}
                              </td>

                              {/* Email */}
                              <td
                                className="px-4 py-3 text-xs"
                                style={{ color: '#374151', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {user?.email || <span style={{ color: '#9ca3af' }}>—</span>}
                              </td>

                              {/* Company */}
                              <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: '#374151' }}>
                                {user?.company_name || <span style={{ color: '#9ca3af' }}>—</span>}
                              </td>

                              {/* GSTIN / PAN */}
                              <td className="px-4 py-3 whitespace-nowrap text-xs font-mono" style={{ color: '#374151' }}>
                                {['transporter','sales_agent'].includes(user?.role)
                                  ? (user?.pan || <span style={{ color: '#9ca3af' }}>—</span>)
                                  : (user?.gstin || <span style={{ color: '#9ca3af' }}>—</span>)}
                              </td>

                              {/* Role — static badge for admin, dropdown for others */}
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user?.role === 'admin' ? (
                                  <span
                                    className="px-2 py-1 rounded-lg text-xs font-semibold"
                                    style={{ backgroundColor: ROLE_COLORS.admin.bg, color: ROLE_COLORS.admin.color, border: `1px solid ${ROLE_COLORS.admin.color}` }}
                                  >
                                    Admin
                                  </span>
                                ) : (
                                  <select
                                    value={user?.role}
                                    onChange={e => handleRoleChange(user?.user_id, e?.target?.value)}
                                    className="text-xs px-2 py-1 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400 font-semibold cursor-pointer"
                                    style={{
                                      borderColor:     roleColor?.color,
                                      color:           roleColor?.color,
                                      backgroundColor: roleColor?.bg,
                                    }}
                                  >
                                    {ROLE_OPTIONS?.map(opt => (
                                      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                                    ))}
                                  </select>
                                )}
                              </td>

                              {/* Factory Directory secondary permission */}
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user?.role === 'admin' ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                                    style={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                                    title="Admins always have access"
                                  >
                                    Always On
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!user?.factory_directory_enabled}
                                    aria-label={`Factory Directory access for ${user?.full_name || user?.email || 'user'}`}
                                    disabled={fdPermissionSaving === user?.user_id}
                                    onClick={() =>
                                      handleFactoryDirectoryToggle(
                                        user,
                                        !user?.factory_directory_enabled
                                      )
                                    }
                                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50"
                                    style={{
                                      backgroundColor: user?.factory_directory_enabled
                                        ? '#059669'
                                        : '#d1d5db',
                                    }}
                                  >
                                    <span
                                      className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
                                      style={{
                                        transform: user?.factory_directory_enabled
                                          ? 'translateX(22px)'
                                          : 'translateX(4px)',
                                      }}
                                    />
                                  </button>
                                )}
                              </td>

                              {/* DO Finance secondary permission */}
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={!!user?.do_finance_enabled}
                                  aria-label={`DO Finance access for ${user?.full_name || user?.email || 'user'}`}
                                  disabled={dfPermissionSaving === user?.user_id}
                                  onClick={() =>
                                    handleDoFinanceToggle(
                                      user,
                                      !user?.do_finance_enabled
                                    )
                                  }
                                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50"
                                  style={{
                                    backgroundColor: user?.do_finance_enabled
                                      ? '#059669'
                                      : '#d1d5db',
                                  }}
                                >
                                  <span
                                    className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
                                    style={{
                                      transform: user?.do_finance_enabled
                                        ? 'translateX(22px)'
                                        : 'translateX(4px)',
                                    }}
                                  />
                                </button>
                              </td>

                              {/* Status badge */}
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold"
                                  style={{
                                    backgroundColor: user?.is_active ? '#d1fae5' : '#fee2e2',
                                    color:           user?.is_active ? '#065f46' : '#991b1b',
                                  }}
                                >
                                  {user?.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>

                              {/* Registered date */}
                              <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: '#6b7280' }}>
                                {formatDate(user?.created_at)}
                              </td>

                              {/* Deactivate / Activate + Archive — hidden for admin */}
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user?.role !== 'admin' && (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => setDeactivateConfirm(user)}
                                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white transition hover:opacity-90 whitespace-nowrap"
                                      style={{ backgroundColor: user?.is_active ? '#dc2626' : '#059669' }}
                                    >
                                      <Icon name={user?.is_active ? 'UserX' : 'UserCheck'} size={11} />
                                      {user?.is_active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                      onClick={() => setArchiveConfirm(user)}
                                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition hover:opacity-90 whitespace-nowrap"
                                      style={{ backgroundColor: user?.is_archived ? '#e0e7ff' : '#f3f4f6', color: user?.is_archived ? '#3730a3' : '#4b5563' }}
                                    >
                                      <Icon name={user?.is_archived ? 'ArchiveRestore' : 'Archive'} size={11} />
                                      {user?.is_archived ? 'Unarchive' : 'Archive'}
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {filteredUsers?.map(user => {
                    const roleColor = ROLE_COLORS[user?.role] || ROLE_COLORS.buyer;
                    return (
                      <div key={user?.user_id} className="bg-white rounded-xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                              style={{ backgroundColor: '#1e4a72' }}
                            >
                              {user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-semibold text-sm" style={{ color: '#1f2937' }}>
                                {user?.full_name || '—'}
                              </p>
                              <p className="text-xs" style={{ color: '#9ca3af' }}>
                                {user?.company_name || '—'}
                              </p>
                            </div>
                          </div>
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{
                              backgroundColor: user?.is_active ? '#d1fae5' : '#fee2e2',
                              color:           user?.is_active ? '#065f46' : '#991b1b',
                            }}
                          >
                            {user?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 text-xs mb-3" style={{ color: '#6b7280' }}>
                          <div><span className="font-medium">Mobile:</span> {user?.mobile_phone || '—'}</div>
                          <div><span className="font-medium">Email:</span> {user?.email || '—'}</div>
                          <div>
                            <span className="font-medium">{['transporter','sales_agent'].includes(user?.role) ? 'PAN:' : 'GSTIN:'}</span>{' '}
                            {(['transporter','sales_agent'].includes(user?.role) ? user?.pan : user?.gstin) || '—'}
                          </div>
                          <div><span className="font-medium">Registered:</span> {formatDate(user?.created_at)}</div>
                        </div>

                        {/* Role — static badge for admin, dropdown for others */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs font-medium" style={{ color: '#374151' }}>Role:</span>
                          {user?.role === 'admin' ? (
                            <span
                              className="text-xs px-2 py-1.5 rounded-lg font-semibold"
                              style={{ backgroundColor: ROLE_COLORS.admin.bg, color: ROLE_COLORS.admin.color, border: `1px solid ${ROLE_COLORS.admin.color}` }}
                            >
                              Admin
                            </span>
                          ) : (
                            <select
                              value={user?.role}
                              onChange={e => handleRoleChange(user?.user_id, e?.target?.value)}
                              className="flex-1 text-xs px-2 py-1.5 rounded-lg border focus:outline-none font-semibold cursor-pointer"
                              style={{
                                borderColor:     roleColor?.color,
                                color:           roleColor?.color,
                                backgroundColor: roleColor?.bg,
                              }}
                            >
                              {ROLE_OPTIONS?.map(opt => (
                                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                              ))}
                            </select>
                          )}
                        </div>

                        {/* Factory Directory permission */}
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className="text-xs font-medium" style={{ color: '#374151' }}>
                            Factory Directory
                          </span>
                          {user?.role === 'admin' ? (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-semibold"
                              style={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                            >
                              Always On
                            </span>
                          ) : (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={!!user?.factory_directory_enabled}
                              disabled={fdPermissionSaving === user?.user_id}
                              onClick={() =>
                                handleFactoryDirectoryToggle(
                                  user,
                                  !user?.factory_directory_enabled
                                )
                              }
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50"
                              style={{
                                backgroundColor: user?.factory_directory_enabled
                                  ? '#059669'
                                  : '#d1d5db',
                              }}
                            >
                              <span
                                className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
                                style={{
                                  transform: user?.factory_directory_enabled
                                    ? 'translateX(22px)'
                                    : 'translateX(4px)',
                                }}
                              />
                            </button>
                          )}
                        </div>

                        {/* DO Finance permission */}
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className="text-xs font-medium" style={{ color: '#374151' }}>
                            DO Finance
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!user?.do_finance_enabled}
                            disabled={dfPermissionSaving === user?.user_id}
                            onClick={() =>
                              handleDoFinanceToggle(
                                user,
                                !user?.do_finance_enabled
                              )
                            }
                            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50"
                            style={{
                              backgroundColor: user?.do_finance_enabled
                                ? '#059669'
                                : '#d1d5db',
                            }}
                          >
                            <span
                              className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
                              style={{
                                transform: user?.do_finance_enabled
                                  ? 'translateX(22px)'
                                  : 'translateX(4px)',
                              }}
                            />
                          </button>
                        </div>

                        {user?.role !== 'admin' && (
                          <div className="space-y-2">
                            <button
                              onClick={() => setDeactivateConfirm(user)}
                              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                              style={{ backgroundColor: user?.is_active ? '#dc2626' : '#059669' }}
                            >
                              <Icon name={user?.is_active ? 'UserX' : 'UserCheck'} size={13} />
                              {user?.is_active ? 'Deactivate User' : 'Activate User'}
                            </button>
                            <button
                              onClick={() => setArchiveConfirm(user)}
                              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition hover:opacity-90"
                              style={{ backgroundColor: user?.is_archived ? '#e0e7ff' : '#f3f4f6', color: user?.is_archived ? '#3730a3' : '#4b5563' }}
                            >
                              <Icon name={user?.is_archived ? 'ArchiveRestore' : 'Archive'} size={13} />
                              {user?.is_archived ? 'Unarchive User' : 'Archive User'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {/* ── Section 3: Quick Access ── */}
          <section>
            <h2 className="text-lg font-bold mb-4" style={{ color: '#1f2937' }}>{t('adminDashboard.quickActions')}</h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/quota-allocation-pricing-center')}
                className="flex items-center justify-center gap-3 px-8 py-5 rounded-2xl text-white font-bold text-base shadow-md transition-all hover:opacity-90 hover:shadow-lg active:scale-95"
                style={{ backgroundColor: '#1e4a72', minWidth: '320px' }}
              >
                <Icon name="LayoutDashboard" size={22} />
                Open Full {t('adminDashboard.quotaManager')}
                <Icon name="ArrowRight" size={18} />
              </button>
              <button
                onClick={() => navigate('/admin-commission-management')}
                className="flex items-center justify-center gap-3 px-8 py-5 rounded-2xl text-white font-bold text-base shadow-md transition-all hover:opacity-90 hover:shadow-lg active:scale-95"
                style={{ backgroundColor: '#7c3aed', minWidth: '280px' }}
              >
                <Icon name="Users" size={22} />
                Sales Agents & Commissions
                <Icon name="ArrowRight" size={18} />
              </button>
              <button
                onClick={() => navigate('/reports-generation-center')}
                className="flex items-center justify-center gap-3 px-8 py-5 rounded-2xl text-white font-bold text-base shadow-md transition-all hover:opacity-90 hover:shadow-lg active:scale-95"
                style={{ backgroundColor: '#065f46', minWidth: '260px' }}
              >
                <Icon name="BarChart2" size={22} />
                {t('adminDashboard.reports')}
                <Icon name="ArrowRight" size={18} />
              </button>
            </div>
            <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>
              Access all Quota Manager tabs and Reports with full permissions
            </p>
          </section>

          </>)}

          {activeTab === 'vehicles' && (<>
          {/* ── Section 4: Vehicle Lookup ── */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>Vehicle Lookup</h2>
              <p className="text-sm" style={{ color: '#6b7280' }}>Admin only — VAHAN, FASTag and toll crossings for any vehicle</p>
            </div>

            {/* Vehicle Number Input */}
            <div className="bg-white rounded-2xl border shadow-sm p-5 mb-4" style={{ borderColor: '#e5e7eb' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#374151' }}>Vehicle Number</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={vehicleNumber}
                  onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleVehicleLookup()}
                  placeholder="e.g. MH09AB1234"
                  className="flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: '#d1d5db', color: '#1f2937' }}
                />
                <button
                  onClick={handleVehicleLookup}
                  disabled={vehicleLookupLoading || !vehicleNumber.trim()}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#1e4a72' }}
                >
                  {vehicleLookupLoading
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Icon name="Search" size={15} />}
                  {vehicleLookupLoading ? 'Checking...' : 'Search'}
                </button>
              </div>
              {vehicleLookupError && (
                <p className="text-xs mt-2" style={{ color: '#dc2626' }}>{vehicleLookupError}</p>
              )}
            </div>

            {/* Vehicle Results */}
            {vehicleData && (
              <div className="space-y-4 mb-6">

                {/* VAHAN */}
                <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#dbeafe' }}>
                      <Icon name="FileText" size={16} color="#1e4a72" />
                    </div>
                    <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>VAHAN — RC Details</h3>
                  </div>
                  {vehicleData.vahan?.rc_regn_no ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        ['Reg No',       vehicleData.vahan.rc_regn_no],
                        ['Owner',        vehicleData.vahan.rc_owner_name],
                        ['Class',        vehicleData.vahan.rc_vh_class_desc],
                        ['RC Status',    vehicleData.vahan.rc_status],
                        ['Valid Upto',   vehicleData.vahan.rc_regn_upto],
                        ['Insurance',    vehicleData.vahan.rc_insurance_upto],
                        ['Fitness',      vehicleData.vahan.rc_fit_upto],
                        ['PUCC',         vehicleData.vahan.rc_pucc_upto],
                        ['Tax',          vehicleData.vahan.rc_tax_upto],
                        ['Blacklist',    vehicleData.vahan.rc_blacklist_status],
                        ['NCRB',         vehicleData.vahan.rc_ncrb_status],
                        ['GVW',          vehicleData.vahan.rc_gvw],
                        ['Permit Type',  vehicleData.vahan.rc_permit_type],
                        ['Permit Valid', vehicleData.vahan.rc_permit_valid_upto],
                        ['NP No',        vehicleData.vahan.rc_np_no],
                        ['NP Upto',      vehicleData.vahan.rc_np_upto],
                        ['Financer',     vehicleData.vahan.rc_financer],
                      ].map(([label, val]) => (
                        <div key={label}>
                          <p className="text-xs font-medium mb-0.5" style={{ color: '#9ca3af' }}>{label}</p>
                          <p className="text-sm font-semibold" style={{ color: val ? '#1f2937' : '#d1d5db' }}>
                            {val || '—'}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: '#9ca3af' }}>No VAHAN data found for this vehicle.</p>
                  )}
                </div>

                {/* FASTag Status */}
                <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                        <Icon name="CreditCard" size={16} color="#065f46" />
                      </div>
                      <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>FASTag Status</h3>
                    </div>
                    {vehicleData.fastag?.fastag_active === true && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#d1fae5', color: '#065f46' }}>✅ Active</span>
                    )}
                    {vehicleData.fastag?.fastag_active === false && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>❌ Inactive / No Record</span>
                    )}
                  </div>
                  {vehicleData.fastag && Array.isArray(vehicleData.fastag.data) && vehicleData.fastag.data.length > 0 ? (
                    <div className="flex flex-wrap gap-6">
                      {vehicleData.fastag.data.map((item, i) => (
                        <div key={i}>
                          <p className="text-xs font-medium mb-0.5" style={{ color: '#9ca3af' }}>{item.name}</p>
                          <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>{String(item.value) || '—'}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: '#9ca3af' }}>No FASTag data found.</p>
                  )}
                </div>

                {/* Toll Crossings */}
                <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                      <Icon name="MapPin" size={16} color="#92400e" />
                    </div>
                    <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Toll Crossings</h3>
                  </div>
                  {Array.isArray(vehicleData.toll?.crossings) && vehicleData.toll.crossings.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold" style={{ color: '#6b7280' }}>{vehicleData.toll.crossings.length} crossings recorded</span>
                        <a
                          href={(() => {
                            const coords = vehicleData.toll.crossings.map(tc => {
                              if (!tc.tollPlazaGeocode) return null;
                              const parts = tc.tollPlazaGeocode.split(',').map(s => parseFloat(s.trim()));
                              return (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) ? parts.join(',') : null;
                            }).filter(Boolean);
                            if (!coords.length) return '#';
                            if (coords.length === 1) return `https://www.google.com/maps?q=${coords[0]}`;
                            return `https://www.google.com/maps/dir/${coords.join('/')}`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                          style={{ backgroundColor: '#1e4a72' }}
                        >
                          <span>🗺</span> Full Route ↗
                        </a>
                      </div>
                      <div ref={adminMapContainerRef} style={{ height: '300px', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden', background: '#f1f5f9' }} />
                      <div className="space-y-2">
                      {vehicleData.toll.crossings.map((tc, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: '#fffbf5' }}>
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: '#92400e' }}
                          >
                            {i + 1}
                          </div>
                          <div className="flex-1 grid grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                            <div><span style={{ color: '#9ca3af' }}>Plaza: </span><span style={{ color: '#1f2937', fontWeight: 600 }}>{tc.tollPlazaName || '—'}</span></div>
                            <div><span style={{ color: '#9ca3af' }}>Time: </span><span style={{ color: '#1f2937', fontWeight: 600 }}>{tc.readerReadTime || '—'}</span></div>
                            <div><span style={{ color: '#9ca3af' }}>Direction: </span><span style={{ color: '#1f2937', fontWeight: 600 }}>{tc.laneDirection || '—'}</span></div>
                            <div><span style={{ color: '#9ca3af' }}>Type: </span><span style={{ color: '#1f2937', fontWeight: 600 }}>{tc.vehicleType || '—'}</span></div>
                            <div><span style={{ color: '#9ca3af' }}>Geocode: </span><span style={{ color: '#1f2937', fontWeight: 600 }}>{tc.tollPlazaGeocode || '—'}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                    </>
                  ) : (
                    <p className="text-sm" style={{ color: '#9ca3af' }}>No toll crossings recorded yet.</p>
                  )}
                </div>

              </div>
            )}

            {/* DL / Driver Lookup — independent */}
            <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#ede9fe' }}>
                  <Icon name="IdCard" size={16} color="#7c3aed" />                  
                </div>
                <div>
                  <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Driver Licence Check</h3>
                  <p className="text-xs" style={{ color: '#9ca3af' }}>Enter DL number to verify via Sarathi — independent of vehicle search above</p>
                </div>
              </div>
              <div className="flex gap-3 mb-3">
                <input
                  type="text"
                  value={dlNumber}
                  onChange={e => setDlNumber(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleDriverLookup()}
                  placeholder="e.g. MH0120110012345"
                  className="flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  style={{ borderColor: '#d1d5db', color: '#1f2937' }}
                />
                <input
                  type="date"
                  value={dlDob}
                  onChange={e => setDlDob(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  style={{ borderColor: '#d1d5db', color: '#1f2937' }}
                /> 
                <button
                  onClick={handleDriverLookup}
                  disabled={driverLoading || !dlNumber.trim()}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#7c3aed' }}
                >
                  {driverLoading
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Icon name="Search" size={15} />}
                  {driverLoading ? 'Checking...' : 'Check Driver'}
                </button>
              </div>
              {driverError && (
                <p className="text-xs mb-2" style={{ color: '#dc2626' }}>{driverError}</p>
              )}
              {driverData && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-3 border-t" style={{ borderColor: '#f3f4f6' }}>
                  {[
                    ['DL Number',       driverData.data?.dlLicno],
                    ['Status',          driverData.data?.dlStatus],
                    ['Transport Valid', driverData.data?.dlTrValdtoDate],
                    ['Vehicle Classes', driverData.data?.covdesc],
                    ['RTA Office',      driverData.data?.olaName],                    
                    ['Issue Date',  driverData.data?.dlIssuedt],
                    ['State Name',  driverData.data?.stateName],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <p className="text-xs font-medium mb-0.5" style={{ color: '#9ca3af' }}>{label}</p>
                      <p className="text-sm font-semibold" style={{ color: val ? '#1f2937' : '#d1d5db' }}>{val || '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </section>
          {/* ── Section 5: Vehicle Change Requests ── */}
          <section>
            <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#e5e7eb' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f3e8ff' }}>
                  <Icon name="RefreshCw" size={16} color="#7c3aed" />
                </div>
                <div>
                  <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Vehicle Change Requests</h3>
                  <p className="text-xs" style={{ color: '#9ca3af' }}>Pending vehicle substitution approvals across all bookings</p>
                </div>
              </div>
              <VehicleSubstitutionsTab />
            </div>
          </section>
          </>)}

          {activeTab === 'do_finance' && <DoFinanceTab />}
          {activeTab === 'api_lab' && <ApiLabTab />}
          {activeTab === 'review_queue' && <AdminReviewQueueTab />}
          {activeTab === 'visitor_stats' && <VisitorStatsTab />}

        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign Out?</h2>
            <p className="text-sm text-gray-500 mb-6">You will be logged out of the Admin dashboard.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={confirmLogout} className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors">Yes, Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;

